import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { message, Upload, Input, Button, Image, Select } from "antd";
import { Helmet } from "react-helmet";
import { Layout } from "../pages/app/Layout";
import { Loader } from "../components/Loader";
import { PBox, PH4, PImg, PText, PH2, PH1 } from "../theme/BaseTheme";
import { refetchUser } from "../../actions/auth";
import ChangePlanModal from "../components/app/ChangePlanModal";
import PaymentConfirmModal from "../components/app/PaymentConfirmModal";
import { SellFaq } from "../sections/SellFaq";
import { interior_design_generator_faq_data } from "./InteriorDesignLanding";
import { generateInteriorDesign } from "../../actions/interiorDesign";
import InteriorDesignDownloadModal from "./InteriorDesignDownloadModal";
import { UploadErrorMessages } from "../../data/messages";

const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

const roomTypeOptions = [
    { value: "living room", label: "Living Room" },
    { value: "dining room", label: "Dining Room" },
    { value: "bedroom", label: "Bedroom" },
    { value: "kitchen", label: "Kitchen" },
    { value: "bathroom", label: "Bathroom" },
    { value: "office", label: "Office" },
    { value: "basement", label: "Basement" },
    { value: "garage", label: "Garage" },
    { value: "patio", label: "Patio" },
    { value: "gaming room", label: "Gaming Room" },
    { value: "outdoor", label: "Outdoor" },
];

const roomThemeOptions = [
    { value: "modern", label: "Modern" },
    { value: "traditional", label: "Traditional" },
    { value: "industrial", label: "Industrial" },
    { value: "rustic", label: "Rustic" },
    { value: "bohemian", label: "Bohemian" },
    { value: "minimalist", label: "Minimalist" },
    { value: "vintage", label: "Vintage" },
    { value: "farmhouse", label: "Farmhouse" },
    { value: "coastal", label: "Coastal" },
    { value: "tropical", label: "Tropical" },
    { value: "retro", label: "Retro" },
    { value: "mid-century modern", label: "Mid-Century Modern" },
    { value: "neoclassical", label: "Neoclassical" },
    { value: "baroque", label: "Baroque" },
    { value: "gothic", label: "Gothic" },
    { value: "renaissance", label: "Renaissance" },
    { value: "tribal", label: "Tribal" },
];

class InteriorDesignApp extends Component {
    canvasRef = React.createRef();

    constructor(props) {
        super(props);
        this.highlightRef = React.createRef();
        this.resultRef = React.createRef();
        this.state = {
            isLoading: true,
            brushSize: 30,
            isGenerating: false,
            isDownloading: false,
            uploadedImage: "",
            interiorDesign: "",
            prompt: "",
            fileList: [],
            isChangePlanModalOpen: false,
            isPaymentConfirmModalOpen: false,
            newPlan: "",
            shouldScrollToResult: false,
            isDownloadModalOpen: false,
            roomType: "living room",
            roomTheme: "modern",
        };
        this.handleUpload = this.handleUpload.bind(this);
        this.handleDownloadModalOk = this.handleDownloadModalOk.bind(this);
        this.handleDownloadModalCancel =
            this.handleDownloadModalCancel.bind(this);
    }

    handleUpload = async ({ file }) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        await this.setState({
            uploadedImage: file.url || file.preview,
        });
    };

    async componentDidMount() {
        await this.setState({ isLoading: false });
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.shouldScrollToResult && this.highlightRef.current) {
            this.highlightRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            this.setState({ shouldScrollToResult: false });
        }
    }

    handleDownloadModalOk = () => {
        this.setState({ isDownloadModalOpen: false });
    };

    handleDownloadModalCancel = () => {
        this.setState({ isDownloadModalOpen: false });
    };

    render() {
        if (this.state.isLoading) {
            return <Loader />;
        }
        return (
            <Layout>
                <Helmet>
                    <title>Interior Design Generator App</title>
                    <meta
                        name="description"
                        content="AI-powered interior design generator to redesign homes."
                    />
                    <meta
                        name="keywords"
                        content="Interior Design Generator, AI Interior Design Generator, Interior Design Generator APP, Interior Design Online Generator, Image Processing, AI Image Editing, Patternful, Patternful AI"
                    />
                </Helmet>
                <PBox css={styles}>
                    <PBox className="app-content">
                        <PBox className="app-content-title">
                            <PH1>AI Interior Design Generator</PH1>
                            <PText>
                                Generate interior designs for your home.
                            </PText>
                        </PBox>
                        <PBox className="app-content-control">
                            <PBox className="interior-design-upload-image">
                                <PBox className="upload-section">
                                    <PBox className="interior-design-upload">
                                        <Upload.Dragger
                                            onChange={async (info) => {
                                                await this.handleUpload(info);
                                            }}
                                            showUploadList={false}
                                            listType="picture-card"
                                            maxCount={1}
                                            beforeUpload={async (file) => {
                                                const isJpgOrPng =
                                                    file.type ===
                                                        "image/jpeg" ||
                                                    file.type === "image/png";
                                                if (!isJpgOrPng) {
                                                    message.error(
                                                        UploadErrorMessages.JPG_OR_PNG
                                                    );
                                                }
                                                const isLt4M =
                                                    file.size / 1024 / 1024 < 4;
                                                if (!isLt4M) {
                                                    message.error(
                                                        UploadErrorMessages.IMAGE_SIZE_4MB
                                                    );
                                                }
                                                return (
                                                    (isJpgOrPng && isLt4M) ||
                                                    Upload.LIST_IGNORE
                                                );
                                            }}
                                        >
                                            {this.state.uploadedImage !==
                                                "" && (
                                                <PImg
                                                    className="background-removed-image"
                                                    src={
                                                        this.state.uploadedImage
                                                    }
                                                />
                                            )}
                                            {this.state.uploadedImage ===
                                                "" && (
                                                <PText>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="#000000"
                                                        viewBox="0 0 256 256"
                                                    >
                                                        <path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0ZM93.66,85.66,120,59.31V152a8,8,0,0,0,16,0V59.31l26.34,26.35a8,8,0,0,0,11.32-11.32l-40-40a8,8,0,0,0-11.32,0l-40,40A8,8,0,0,0,93.66,85.66Z"></path>
                                                    </svg>
                                                    <br />
                                                    Click to upload your image,
                                                    <br />
                                                    or drag and drop your image
                                                    into this area.
                                                </PText>
                                            )}
                                        </Upload.Dragger>
                                    </PBox>
                                </PBox>
                                <PBox className="input-section">
                                    <PText className="control-section-label">
                                        Room Type
                                    </PText>
                                    <Select
                                        style={{
                                            width: "100%",
                                        }}
                                        options={roomTypeOptions}
                                        onChange={async (value) => {
                                            this.setState({
                                                roomType: value,
                                            });
                                        }}
                                    />
                                    <PText className="control-section-label">
                                        Room Theme
                                    </PText>
                                    <Select
                                        style={{
                                            width: "100%",
                                        }}
                                        options={roomThemeOptions}
                                        onChange={async (value) => {
                                            this.setState({ roomTheme: value });
                                        }}
                                    />{" "}
                                    <PText className="control-section-label">
                                        Prompt - How you would like the room to
                                        be
                                    </PText>
                                    <Input.TextArea
                                        autoSize={{ minRows: 4, maxRows: 4 }}
                                        placeholder="Enter your prompt"
                                        draggable={false}
                                        onChange={(e) => {
                                            this.setState({
                                                prompt: e.target.value,
                                            });
                                        }}
                                    ></Input.TextArea>
                                </PBox>
                            </PBox>
                            <PBox className="generate-button-container">
                                <Button
                                    type="primary"
                                    onClick={async () => {
                                        if (
                                            this.props.userAccount?.credits +
                                                this.props.userAccount
                                                    ?.roll_over_credits <
                                            5
                                        ) {
                                            message.error(
                                                "Your credit balance is not enough. Please consider upgrading your plan or buying more credits.",
                                                3
                                            );
                                        } else {
                                            if (
                                                this.state.uploadedImage === ""
                                            ) {
                                                message.error(
                                                    "Please upload a home image. ",
                                                    3
                                                );
                                            } else if (
                                                this.state.prompt === ""
                                            ) {
                                                message.error(
                                                    "Prompt cannot be empty. ",
                                                    3
                                                );
                                            } else {
                                                await this.setState({
                                                    isGenerating: true,
                                                });
                                                message.open({
                                                    key: "gen_interior_design",
                                                    type: "loading",
                                                    content:
                                                        "Start generating interior designs. This can take a while.",
                                                    duration: 0,
                                                });
                                                await this.props.generateInteriorDesign(
                                                    this.state.uploadedImage,
                                                    this.state.prompt,
                                                    this.state.roomType,
                                                    this.state.roomTheme
                                                );
                                                message.destroy(
                                                    "gen_interior_design"
                                                );
                                                await this.props.refetchUser();
                                                await this.setState({
                                                    isGenerating: false,
                                                    shouldScrollToResult: true,
                                                });
                                            }
                                        }
                                    }}
                                    loading={this.state.isGenerating}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    Generate{" "}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="#000000"
                                        viewBox="0 0 256 256"
                                        style={{
                                            fill: "#d48806",
                                            backgroundColor: "#ffe58f",
                                            padding: "0.125rem 0.25rem",
                                            borderRadius: "0.25rem",
                                            width: "1rem",
                                            height: "1rem",
                                            marginLeft: "0.5rem",
                                            marginRight: "0.25rem",
                                        }}
                                    >
                                        <path d="M200,48H136V16a8,8,0,0,0-16,0V48H56A32,32,0,0,0,24,80V192a32,32,0,0,0,32,32H200a32,32,0,0,0,32-32V80A32,32,0,0,0,200,48Zm16,144a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V80A16,16,0,0,1,56,64H200a16,16,0,0,1,16,16Zm-52-56H92a28,28,0,0,0,0,56h72a28,28,0,0,0,0-56Zm-28,16v24H120V152ZM80,164a12,12,0,0,1,12-12h12v24H92A12,12,0,0,1,80,164Zm84,12H152V152h12a12,12,0,0,1,0,24ZM72,108a12,12,0,1,1,12,12A12,12,0,0,1,72,108Zm88,0a12,12,0,1,1,12,12A12,12,0,0,1,160,108Z"></path>
                                    </svg>
                                    x5
                                </Button>
                                <Button type="ghost" href="/interior-designs/1">
                                    View My Interior Designs
                                </Button>
                            </PBox>

                            {this.props.interiorDesign !== undefined && (
                                <>
                                    <PBox
                                        className="generate-interior-design-content"
                                        ref={this.highlightRef}
                                    >
                                        <PBox className="binary-mask-sketch-canva">
                                            <Image
                                                src={this.props.interiorDesign}
                                                crossOrigin={
                                                    process.env.NODE_ENV ===
                                                    "development"
                                                        ? "anonymous"
                                                        : ""
                                                }
                                            />
                                        </PBox>
                                    </PBox>{" "}
                                    <PBox className="generate-button-container">
                                        <Button
                                            type="primary"
                                            onClick={async () => {
                                                await this.setState({
                                                    isDownloadModalOpen: true,
                                                });
                                            }}
                                        >
                                            Download
                                        </Button>
                                    </PBox>
                                </>
                            )}

                            <SellFaq
                                data={interior_design_generator_faq_data}
                            />
                        </PBox>
                        {this.state.isChangePlanModalOpen && (
                            <ChangePlanModal
                                isChangePlanModalOpen={
                                    this.state.isChangePlanModalOpen
                                }
                                handleChangePlanModalOk={async () => {
                                    await this.setState({
                                        isPaymentConfirmModalOpen: true,
                                    });
                                    await this.setState({
                                        isChangePlanModalOpen: false,
                                    });
                                }}
                                handleChangePlanModalCancel={() => {
                                    this.setState({
                                        isChangePlanModalOpen: false,
                                    });
                                }}
                                setNewPlan={(value) => {
                                    this.setState({ newPlan: value });
                                }}
                            />
                        )}
                        {this.state.isDownloadModalOpen && (
                            <InteriorDesignDownloadModal
                                interiorDesignUUID={this.props.uuid}
                                isDownloadModalOpen={
                                    this.state.isDownloadModalOpen
                                }
                                handleDownloadModalOk={
                                    this.handleDownloadModalOk
                                }
                                handleDownloadModalCancel={
                                    this.handleDownloadModalCancel
                                }
                            />
                        )}
                        {this.state.isPaymentConfirmModalOpen && (
                            <PaymentConfirmModal
                                newPlan={this.state.newPlan}
                                isPaymentConfirmModalOpen={
                                    this.state.isPaymentConfirmModalOpen
                                }
                                handlePaymentConfirmModalOk={() =>
                                    this.setState({
                                        isPaymentConfirmModalOpen: false,
                                    })
                                }
                                handlePaymentConfirmModalCancel={() =>
                                    this.setState({
                                        isPaymentConfirmModalOpen: false,
                                    })
                                }
                            />
                        )}
                    </PBox>
                </PBox>
            </Layout>
        );
    }
}

const styles = {
    minHeight: "48rem",
    "& .app-content-title": {
        "& h1": {
            textAlign: "center",
        },
        "& p": {
            textAlign: "center",
        },
        margin: "4rem auto",
    },
    "& .app-content-control": {
        margin: "0 auto",
    },
    "& .generate-button-container": {
        display: "flex",
        justifyContent: "center",
        margin: "2rem 0",
        "& button": {
            margin: "0 0.5rem",
        },
    },
    "& .interior-design-upload-image": {
        margin: "2rem auto 2rem auto",
        maxWidth: "64rem",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "2rem",
        alignItems: "start",
        "@media (max-width: 768px)": {
            gridTemplateColumns: "1fr",
            gap: "1rem",
        },

        "& .upload-section": {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        },
        "& .input-section": {
            display: "flex",
            flexDirection: "column",
            "& .control-section-label": {
                fontWeight: "600",
                fontSize: "14px",
                color: "$colors$text",
                marginBottom: "1rem",
            },
        },
        "& .interior-design-upload": {
            marginBottom: "1rem",
            width: "100%",
            "& svg": {
                width: "3rem",
                height: "3rem",
                fill: "$colors$primary",
            },
            "& span": {
                padding: "0",
                width: "100%",
                height: "20rem",
                display: "block",
                margin: "0",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            },
        },
        "& .background-removed-image": {
            //maxWidth: "12rem",
            maxHeight: "14rem",
            margin: "auto 1rem",
        },
        "& h2": {
            marginBottom: "3rem",
        },
    },
    "& .remove-background-download-buttons": {
        margin: "5rem auto",
        display: "flex",
        justifyContent: "center",
        "& button": {
            margin: "0 0.5rem",
        },
    },
    "& .remove-background-content": {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "2rem",
        marginTop: "8rem",
        marginBottom: "4rem",
        "& button": {
            width: "100%",
        },
    },
    "& .binary-mask-sketch-canva": {
        display: "block",
        "& img": {
            display: "block",
            margin: "auto",
            width: `calc(31rem - 2px)`,
        },
    },
    "& .binary-mask-brush-size": {
        textAlign: "left",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "1rem",
        "& h4": { marginLeft: "0.5rem" },
    },
    "& .binary-mask-result-image": {
        margin: "6rem auto 0 auto",
        "& img": {
            height: "40rem",
            margin: "0 auto",
        },
    },
    "& .upscale-option": {
        display: "flex",
        alignItems: "center",
    },
    "& .caption-before": {
        background: "#fff",
        border: "1px solid #c0c0c0",
        borderRadius: "12px",
        color: "#2e3452",
        opacity: "0.8",
        padding: "12px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        lineHeight: "100%",
        left: "12px",
    },
    "& .caption-after": {
        background: "#fff",
        border: "1px solid #c0c0c0",
        borderRadius: "12px",
        color: "#2e3452",
        opacity: "0.8",
        padding: "12px",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        lineHeight: "100%",
        right: "12px",
    },
    "& .slider-with-animated-handle": {
        position: "relative",
        margin: "auto auto",
        //"& img": { maxHeight: "28rem" },
    },
    "& .remove-background-slider": {
        width: "60rem",
        height: "40rem",
        border: "1px solid $colors$borderColor",
        position: "relative",
        borderRadius: "0.5rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "0 auto",
        "& img": {
            //width: "30rem",
            maxHeight: "40rem",
            margin: "0 auto",
        },
    },
    "& .generate-interior-design-content": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "8rem auto 2rem auto",
        "& img": {
            maxHeight: "40rem",
        },
    },
};

function mapStateToProps(state) {
    return {
        userAccount: state.authReducer?.userAuth?.userAccount,
        interiorDesign:
            state.interiorDesignReducer?.generateInteriorDesign?.generatedImage,
        uuid: state.interiorDesignReducer?.generateInteriorDesign?.uuid,
    };
}

export default connect(mapStateToProps, {
    refetchUser,
    generateInteriorDesign,
})(withRouter(InteriorDesignApp));
